<template>
  <div class="container-fluid py-4">
    <h2 class="mb-4">Office Locations</h2>

    <BButton variant="primary" class="mb-3" @click="openModal">
      <i class="fas fa-plus-circle me-2"></i>Add New Office Location
    </BButton>

    <BCard>
      <BCardHeader>
        <h3 class="mb-0">Office Locations List</h3>
      </BCardHeader>
      <BCardBody>
        <div class="table-responsive">
          <BTable :items="tableItems" :fields="tableFields" :busy="loading">
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <BSpinner class="align-middle me-2"></BSpinner>
                <strong>Loading...</strong>
              </div>
            </template>
            <template #cell(gps)="data">
              <span v-if="data.item?.gps" class="text-primary text-decoration-underline" style="cursor: pointer"
                @click="openMapModal(data.item.gps)"> Map                
              </span>
              <span v-else></span>
            </template>
            <template #cell(actions)="data">
              <div class="btn-group btn-group-sm">
                <BButton variant="outline-primary" @click="editCarehome(data.item)">
                  <i class="fas fa-edit"></i>
                </BButton>
              </div>
            </template>
          </BTable>
        </div>
      </BCardBody>
    </BCard>

    <!-- Modal Form -->
    <BModal v-model="showModal" :title="editing ? 'Edit Office Location' : 'Add Office Location'" size="lg" @hidden="resetForm">
      <BForm @submit.prevent="submitCarehome">
        <BRow class="g-3">
          <BCol md="6">
            <BFormGroup label="Street Name" label-for="streetname">
              <BFormInput id="streetname" v-model="carehomeForm.streetname" required />
            </BFormGroup>
          </BCol>
          <BCol md="6">
            <BFormGroup label="House Number" label-for="housenumber">
              <BFormInput id="housenumber" v-model="carehomeForm.housenumber" required />
            </BFormGroup>
          </BCol>
          <BCol md="6">
            <BFormGroup label="City" label-for="city">
              <BFormInput id="city" v-model="carehomeForm.city" required />
            </BFormGroup>
          </BCol>
          <BCol md="6">
            <BFormGroup label="State" label-for="state">
              <BFormSelect id="state" v-model="carehomeForm.state" :options="usStates" required />
            </BFormGroup>
          </BCol>
          <BCol md="6">
            <BFormGroup label="Zip Code" label-for="zipcode">
              <BFormInput id="zipcode" v-model="carehomeForm.zipcode" required />
            </BFormGroup>
          </BCol>
          <BCol md="6">
            <BFormGroup label="Latitude" label-for="gps-lat">
              <BFormInput type="number" step="any" id="gps-lat" v-model.number="carehomeForm.gpsLat" required
                placeholder="e.g., 32.95" />
            </BFormGroup>
          </BCol>
          <BCol md="6">
            <BFormGroup label="Longitude" label-for="gps-lng">
              <BFormInput type="number" step="any" id="gps-lng" v-model.number="carehomeForm.gpsLng" required
                placeholder="e.g., -96.78" />
            </BFormGroup>
          </BCol>
        </BRow>

      </BForm>
      <template #footer>
        <BButton variant="secondary" @click="showModal = false">Close</BButton>
        <BButton variant="primary" type="submit" @click="submitCarehome" :disabled="formLoading || !formValid">
          <span v-if="formLoading" class="spinner-border spinner-border-sm"></span>
          {{ editing ? 'Update' : 'Add' }} Office Location
        </BButton>
      </template>
    </BModal>

    <MapModal v-model="showMapModal" :gps="selectedLocation" />

    <BToast :modelValue="!!toastMessage" @update:modelValue="toastMessage = ''">
      {{ toastMessage }}
    </BToast>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect } from 'vue';
import { useToast } from 'vue-toastification';
import MapModal from './MapModal.vue';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { GET_CAREHOMES_BY_USER, CREATE_CAREHOME, UPDATE_CAREHOME } from '@/hooks/queries';
import { usStates } from '@/components/values';


const showMapModal = ref(false);
const selectedLocation = ref({ lat: null, lng: null });

const toast = useToast();

const carehomeForm = ref({
  id: null,
  streetname: '',
  housenumber: '',
  city: '',
  state: '',
  zipcode: '',
  gpsLat: null,
  gpsLng: null
});
const showModal = ref(false);
const editing = ref(false);
const formLoading = ref(false);
const toastMessage = ref('');

const tableFields = [
  { key: 'streetname', label: 'Street' },
  { key: 'housenumber', label: 'No.' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipcode', label: 'ZIP' },
  {
    key: 'gps',
    label: 'Location',
  },
  { key: 'actions', label: 'Actions' }
];

const userString = localStorage.getItem('user');
const user = userString ? JSON.parse(userString) : null;
const documentId = user?.documentId || '';
const adminid = user?.id || '';

const { result, loading, refetch } = useQuery(GET_CAREHOMES_BY_USER,{userId: documentId});


const { mutate: createCarehome } = useMutation(CREATE_CAREHOME);
const { mutate: updateCarehome } = useMutation(UPDATE_CAREHOME);



const carehomes = ref([]);

watchEffect(() => {
  if (result.value) {
    carehomes.value = result.value.carehomes.map(entry => ({
      id: entry.documentId,
      ...entry
    }));
  }
});

const tableItems = computed(() => carehomes.value);

const formValid = computed(() => {
  const f = carehomeForm.value;
  return (
    f.streetname &&
    f.housenumber &&
    f.city &&
    f.state &&
    f.zipcode &&
    typeof f.gpsLat === 'number' &&
    typeof f.gpsLng === 'number'
  );
});


const openMapModal = (gps) => {
  selectedLocation.value = gps;
  showMapModal.value = true;
};


const submitCarehome = async () => {
  if (!formValid.value) return;
  formLoading.value = true;
  try {
    const variables = {
      data: {
        streetname: carehomeForm.value.streetname,
        housenumber: carehomeForm.value.housenumber,
        city: carehomeForm.value.city,
        state: carehomeForm.value.state,
        zipcode: carehomeForm.value.zipcode,
	admin: String(adminid),
        gps: {
          lat: carehomeForm.value.gpsLat,
          lng: carehomeForm.value.gpsLng
        },
	users_permissions_users: [user?.documentId]
      }
    };
    if (editing.value) {
     //console.log(adminid);
      await updateCarehome({
        id: carehomeForm.value.id,
        data: variables.data
      });
      toast.success('Carehome updated successfully');
    } else {
      await createCarehome(variables);
      toast.success('Carehome created successfully');
    }
    showModal.value = false;
    refetch();
  } catch (error) {
    toast.error('Failed to save carehome: ' + error.message);
  } finally {
    formLoading.value = false;
  }
};

const editCarehome = (carehome) => {
  carehomeForm.value = {
    id: carehome.id,
    streetname: carehome.streetname,
    housenumber: carehome.housenumber,
    city: carehome.city,
    state: carehome.state,
    zipcode: carehome.zipcode,
    gpsLat: carehome.gps?.lat ?? null,
    gpsLng: carehome.gps?.lng ?? null
  };
  editing.value = true;
  showModal.value = true;
};

const openModal = () => {
  resetForm();
  showModal.value = true;
};

const resetForm = () => {
  carehomeForm.value = {
    id: null,
    streetname: '',
    housenumber: '',
    city: '',
    state: '',
    zipcode: '',
    gpsLat: null,
    gpsLng: null
  };
  editing.value = false;
};
</script>

<style scoped>
.container-fluid {
  max-width: 80%;
}

.table-responsive {
  overflow-x: auto;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}
</style>
